import { z } from 'zod';
import {
  optionalEmail,
  optionalNumber,
  optionalPhone,
  optionalText,
  optionalUrl,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { currencySchema } from './organization.js';
import { businessManagerFilterSchema, type BusinessManagerRef } from './records.js';

/**
 * Suppliers. Zoho's Vendors module, near enough field for field — the address
 * is unprefixed here as it is there, because a vendor has one.
 */
export const createVendorSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  email: optionalEmail(),
  phone: optionalPhone(),
  website: optionalUrl(),
  category: optionalText(120),
  /** The nominal ledger code purchases from this vendor post to. */
  glAccount: optionalText(60),
  description: optionalText(4000),

  /** The person at the vendor to talk to. */
  contactName: optionalText(200),
  /** GSTIN, or whatever tax registration the vendor holds. */
  gstNumber: optionalText(32),
  /** Days from the vendor's bill to when it falls due; blank for none agreed. */
  paymentTermsDays: optionalNumber(z.coerce.number().int().min(0).max(365)),
  /** What the vendor bills in; a purchase order starts in it. */
  defaultCurrency: currencySchema.nullish(),
  isActive: z.coerce.boolean().default(true),

  street: optionalText(200),
  city: optionalText(120),
  state: optionalText(120),
  postalCode: optionalText(32),
  country: optionalText(120),

  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateVendorInput = z.infer<typeof createVendorSchema>;

export const updateVendorSchema = toPatchSchema(createVendorSchema);
export type UpdateVendorInput = z.infer<typeof updateVendorSchema>;

export const VENDOR_SORT_FIELDS = ['createdAt', 'updatedAt', 'name'] as const;

export const listVendorsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(VENDOR_SORT_FIELDS).default('name'),
  businessManagerId: businessManagerFilterSchema.optional(),
  category: z.string().trim().max(120).optional(),
  /** Hide vendors that have been marked inactive. */
  activeOnly: z.stringbool().optional(),
});
export type ListVendorsQuery = z.infer<typeof listVendorsQuerySchema>;

export interface VendorSummary {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  category: string | null;
  contactName: string | null;
  isActive: boolean;
  defaultCurrency: string | null;
  businessManager: BusinessManagerRef | null;
  productCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface VendorDetail extends VendorSummary {
  gstNumber: string | null;
  paymentTermsDays: number | null;
  glAccount: string | null;
  description: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  customFields: Record<string, unknown>;
}

/** A sum in one currency; amounts in different currencies are never added together. */
export interface CurrencyAmount {
  currency: string;
  amount: string;
}

/**
 * Everything linked to a vendor, and totals computed from those records alone.
 *
 * Nothing is counted twice: an open purchase order is a *commitment* until an
 * expense is created from it, at which point it is the expense that counts and
 * the order drops out of the commitment. Cancelled orders and rejected
 * expenses count for nothing.
 */
export interface VendorRelated {
  purchaseOrders: {
    id: string;
    number: string;
    status: string;
    subject: string;
    orderDate: string;
    currency: string;
    total: string;
    expenseId: string | null;
  }[];
  expenses: {
    id: string;
    name: string;
    expenseDate: string;
    currency: string;
    amount: string;
    approvalStatus: string;
    purchaseOrder: { id: string; number: string } | null;
  }[];
  recurringExpenses: {
    id: string;
    name: string;
    currency: string;
    amount: string;
    frequency: string;
    status: string;
  }[];
  /** Money actually paid out: expenses that completed approval, with their bank account and transaction. */
  paymentsMade: {
    expenseId: string;
    name: string;
    paidOn: string;
    currency: string;
    amount: string;
    bankAccount: { id: string; name: string } | null;
    bankTransactionId: string | null;
  }[];
  totals: {
    /** Open purchase orders not yet turned into an expense. */
    committed: CurrencyAmount[];
    /** Expenses recorded against the vendor, rejected ones excluded. */
    expensed: CurrencyAmount[];
    /** Of those, what has completed approval and been paid. */
    paid: CurrencyAmount[];
    /** Expensed but not yet paid. */
    outstanding: CurrencyAmount[];
  };
}
